import { useState } from "react";
import { createFileRoute, redirect } from "@tanstack/react-router";
import {
  ActivityShowPage,
  buildSimilarCityFilters,
  buildSimilarFilters,
  cityMentionTargetsQueryOptions,
  similarKeys,
} from "@majourneys/booking";
import { parseLang } from "@majourneys/shared";
import { api } from "../../../lib/api";
import { useSession } from "../../../lib/auth";
import { useBookingSessionUser } from "../../../features/account/hooks/use-booking-session-user";
import { useSaved } from "../../../features/saved/state/saved-context";
import { SaveSignInModal } from "../../../features/auth/components/save-sign-in-modal";
import type { PendingSave } from "../../../features/auth/lib/post-sign-in";
import { activitiesApi } from "../../../features/activities/api/activities.api";
import { accountKeys } from "../../../features/account/constants/query-keys";
import { activityKeys } from "../../../features/activities/constants/query-keys";
import {
  activityHrefParts,
  resolveLegacyActivitySlug,
} from "../../../features/activities/lib/activity-href";
import { transferHubRedirect } from "../../../features/transport/lib/hub-redirect";
import { addRecentViewed } from "../../../shared/hooks/use-recent-viewed";
import { withApi404 } from "../../../lib/route-loader";
import { useQueryClient } from "@tanstack/react-query";
import { blogApi } from "../../../features/blog/api/blog.api";
import { blogKeys } from "../../../features/blog/constants/query-keys";
import { AmbassadorFavouriteButton } from "../../../features/ambassadors";
import { useActivityVideo } from "../../../features/reels/hooks/use-activity-video";

/**
 * Canonical SEO route: /<lang>/<city>/<category-or-segment>/<slug>
 *
 * Loader prefetches the activity so SSR returns a real HTTP 404 status
 * for unknown slugs (instead of 200 + NotFound page = soft-404 to
 * search engines). React Query caches the result so `useSuspenseQuery`
 * inside the component resolves synchronously.
 *
 * If the requested `$city` or `$category` segment doesn't match the
 * activity's actual canonical parts (e.g. an old URL with `place` as the
 * category for an item that now has a real category, or a stale city slug
 * after a city rename), 301-redirect to the canonical form. Without this
 * guard `/en/<city>/place/<slug>` would serve the page with self-canonical
 * pointing elsewhere — splitting link equity and creating a
 * canonical-tag-only dedupe (weaker than 301).
 */

function MajourneysActivityShow() {
  const { data: session } = useSession();
  const qc = useQueryClient();
  const { slug, lang } = Route.useParams();
  const video = useActivityVideo(slug);
  const loaderData = Route.useLoaderData() as
    | { similarRows?: unknown[]; similarCityRows?: unknown[] }
    | undefined;
  const isSignedIn = !!session?.user;
  const sessionUser = useBookingSessionUser();

  // The shared store, not a second read of /me/saved: this page's heart used
  // to write to the account while every card wrote to localStorage, so the
  // same activity could show saved here and unsaved on the card beside it.
  const { slugs: savedActivitySlugs, toggle: toggleSaved } = useSaved();

  const onToggleWishlist = async (slug: string, _wishlisted: boolean) => {
    await toggleSaved({ slug, title: slug, city: "", price: 0, currency: "MAD" });
    await qc.invalidateQueries({ queryKey: accountKeys.saved() });
  };

  // A signed-out heart opens sign-in with the item on top, and the save
  // completes on its own once they are in (SavedProvider).
  const [saveFor, setSaveFor] = useState<PendingSave | null>(null);

  return (
    <>
    <ActivityShowPage
      isSignedIn={isSignedIn}
      sessionUser={sessionUser}
      savedActivitySlugs={savedActivitySlugs}
      onToggleWishlist={onToggleWishlist}
      onSaveNeedsSignIn={setSaveFor}
      // Tag the entry with the language its title and category are in, so
      // the home rail never shows them on a page in another language.
      onRecentViewed={(item) => addRecentViewed({ ...item, lang })}
      similarItems={loaderData?.similarRows}
      similarCityItems={loaderData?.similarCityRows}
      video={video}
      // Active ambassadors only: "Add to my page" (renders nothing for anyone else).
      renderInlineSlot={({ activity }) => <AmbassadorFavouriteButton activity={activity} />}
    />
    <SaveSignInModal item={saveFor} onClose={() => setSaveFor(null)} />
    </>
  );
}

export const Route = createFileRoute(
  "/$lang/_layout/$city/$category/$slug/",
)({
  loader: async ({ params, context }) => {
    // Renamed slug still indexed by Google → 301 to the live slug instead of 404.
    const liveSlug = resolveLegacyActivitySlug(params.slug);
    if (liveSlug !== params.slug) {
      throw redirect({
        to: "/$lang/$city/$category/$slug",
        params: { ...params, slug: liveSlug },
        // Keep the query (?ref=, dates, guests) through the 301.
        search: (prev: Record<string, unknown>) => prev,
        replace: true,
        statusCode: 301,
      });
    }
    const data = await withApi404(() =>
      context.queryClient.ensureQueryData({
        queryKey: activityKeys.detail(params.slug, params.lang),
        queryFn: () => activitiesApi.getBySlug(params.slug, params.lang),
      }),
    );
    const activity = (data as { data?: unknown }).data ?? data;
    const a = activity as {
      slug: string;
      mode?: string;
      segment?: string | null;
      cityName?: string;
      city?: { slug?: string; name?: string };
      category?: { slug?: string } | null;
      template?: string | null;
    } | null;

    // Transfer routes have no page of their own: 301 to the transport hub
    // with the route preselected, before any of the page's prefetching.
    const hub = transferHubRedirect(a, params.lang);
    if (hub) throw redirect(hub);

    // Prefetch the "Read about {city}" blog articles into the query cache so the
    // reverse links (activity → blog) render in the SSR HTML and are crawlable.
    // Keyed identically to useCityArticles (which reads `activity.cityName`) so
    // the component resolves from cache instead of refetching on the client.
    const cityName = a?.cityName ?? a?.city?.name;
    if (cityName) {
      const l = parseLang(params.lang);
      await context.queryClient
        .ensureQueryData({
          queryKey: [...blogKeys.all, "city", l, cityName, 3] as const,
          queryFn: async () => {
            const res = await blogApi.list(l, { search: cityName, page: 1 });
            return res.data.items.slice(0, 3);
          },
        })
        .catch(() => undefined);
    }

    if (a?.slug) {
      const aMode = a.mode ?? "bookable";
      const aSegment = a.segment ?? null;
      const canonical = activityHrefParts({
        lang: params.lang,
        citySlug: a.city?.slug ?? null,
        categorySlug: a.category?.slug ?? null,
        segment: aMode === "explore" ? aSegment : null,
        slug: a.slug,
      });
      if (
        canonical.params.city !== params.city ||
        canonical.params.category !== params.category
      ) {
        throw redirect({
          to: canonical.to,
          params: canonical.params,
          // Keep the query (?ref=, dates, guests) through the 301.
          search: (prev: Record<string, unknown>) => prev,
          replace: true,
          statusCode: 301,
        });
      }
      // Pre-resolve the same-city venue list that linkifies howToCombine/
      // nearby/stops mentions on explore items so the venue-name links land
      // in the SSR HTML. Non-critical: a failure must never block the page.
      if (aMode === "explore" && a.city?.slug) {
        await context.queryClient
          .ensureQueryData(
            cityMentionTargetsQueryOptions(api, a.city.slug, params.lang),
          )
          .catch(() => undefined);
      }
      // Prefetch SimilarListings' query so the section renders during SSR —
      // it uses a plain useQuery, so without this the crawler-visible HTML
      // carries zero lateral venue links (verified with curl 2026-07-11).
      // Best-effort: a failure must not 500 the page.
      const similarFilters = buildSimilarFilters(
        {
          city: { id: "", name: "", slug: a.city?.slug ?? "" },
          category: a.category?.slug
            ? { id: "", name: "", slug: a.category.slug }
            : null,
          mode: aMode as "bookable" | "explore",
          segment: aSegment,
        },
        params.lang,
      );
      if (similarFilters) {
        const similar = await context.queryClient
          .ensureQueryData({
            queryKey: similarKeys.similar(similarFilters),
            queryFn: () => activitiesApi.list(similarFilters),
          })
          .catch(() => undefined);
        // Return the rows through loader data — the streamed query cache
        // does not resolve inside the server render, so the component needs
        // them as a prop for the links to reach crawler-visible HTML.
        const similarRows = (similar as { data?: unknown[] } | undefined)?.data ?? [];
        // One-activity categories (the single quad tour in Casablanca, the
        // single jet ski, …) match nothing but themselves, so widen to the
        // rest of the city — otherwise the section, and every lateral link
        // on the page, silently disappears.
        const onlySelf = similarRows.filter(
          (row) => (row as { slug?: string }).slug !== a.slug,
        ).length === 0;
        if (!onlySelf) return { data, similarRows, similarCityRows: [] };

        const cityFilters = buildSimilarCityFilters(
          {
            city: { id: "", name: "", slug: a.city?.slug ?? "" },
            mode: aMode as "bookable" | "explore",
          },
          params.lang,
        );
        if (!cityFilters) return { data, similarRows, similarCityRows: [] };
        const inCity = await context.queryClient
          .ensureQueryData({
            queryKey: similarKeys.similar(cityFilters),
            queryFn: () => activitiesApi.list(cityFilters),
          })
          .catch(() => undefined);
        const similarCityRows =
          (inCity as { data?: unknown[] } | undefined)?.data ?? [];
        return { data, similarRows, similarCityRows };
      }
    }
    return { data, similarRows: [], similarCityRows: [] };
  },
  component: MajourneysActivityShow,
});
